import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import "./CreateEvaluationFormModal.css";
import "../edit-user/EditUserModal.css";

import {
  createEvaluationFormApi,
  getEvaluationFormByIdApi,
  listEvaluationFormsApi,
  type CreateEvaluationFormRequest,
  type EvaluationForm,
  type EvaluationQuestion,
  type EvaluationQuestionOption,
  type EvaluationQuestionType,
  type EvaluationAnswererType,
} from "../../../views/evaluations/evaluationFormsApi";

import { getEvaluationQuestionTypeLabel } from "../../../utils/evaluationQuestionTypeLabel";

const XIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type CreateEvaluationFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (form: EvaluationForm) => void;
};

type QuestionDto = NonNullable<CreateEvaluationFormRequest["questions"]>[number];
type OptionDto = NonNullable<QuestionDto["options"]>[number];

type DraftQuestion = QuestionDto & {
  key: string;
  source: "new" | "existing";
  refId?: string;
};

type DraftOption = OptionDto & { key: string };
const normalizeOptionOrders = <T extends { order?: number }>(opts: T[]) => opts.map((o, idx) => ({ ...o, order: idx + 1 }));

const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as any).randomUUID()
    : Math.random().toString(36).slice(2);

const isChoiceType = (t: EvaluationQuestionType) => t === ("SINGLE_CHOICE" as any) || t === ("MULTI_CHOICE" as any);
const isScaleType = (t: EvaluationQuestionType) => t === ("SCALE" as any);
const normalizeQuestionOrders = (qs: DraftQuestion[]) => qs.map((q, idx) => ({ ...q, order: idx + 1 }));

const CreateEvaluationFormModal: React.FC<CreateEvaluationFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<"LOJA" | "FREELANCER" | "NR1">("FREELANCER");
  const [allowedAnswererTypes, setAllowedAnswererTypes] = useState<EvaluationAnswererType[]>(["ADMIN", "SUPERVISOR"]);

  const [loading, setLoading] = useState(false);

  // Selecionar perguntas existentes (copiar de um formulário)
  const [useExistingQuestions, setUseExistingQuestions] = useState(false);
  const [existingForms, setExistingForms] = useState<EvaluationForm[]>([]);
  const [existingFormId, setExistingFormId] = useState("");
  const [existingFormDetail, setExistingFormDetail] = useState<EvaluationForm | null>(null);
  const [existingChecked, setExistingChecked] = useState<Record<string, boolean>>({}); // by question.id

  // Builder de nova pergunta
  const [newType, setNewType] = useState<EvaluationQuestionType>("SINGLE_CHOICE" as any);
  const [newPrompt, setNewPrompt] = useState("");
  const [newRequired, setNewRequired] = useState(false);
  const [newDimension, setNewDimension] = useState("");
  const [newMin, setNewMin] = useState<string>("");
  const [newMax, setNewMax] = useState<string>("");

  const [optLabel, setOptLabel] = useState("");
  const [optValue, setOptValue] = useState("");
  const [newOptions, setNewOptions] = useState<DraftOption[]>([]);

  // Perguntas que serão enviadas no create
  const [selectedQuestions, setSelectedQuestions] = useState<DraftQuestion[]>([]);

  const reqRef = useRef({ list: 0, detail: 0 });

  const isAnonymousCategory = category === "NR1";

  const canSubmit = useMemo(() => {
    return (
      name.trim().length > 0 &&
      category.trim().length > 0 &&
      (isAnonymousCategory || allowedAnswererTypes.length > 0) &&
      !loading
    );
  }, [name, category, isAnonymousCategory, allowedAnswererTypes, loading]);

  // Sugestões de dimensão já usadas, para evitar variações de grafia
  const dimensionSuggestions = useMemo(() => {
    const set = new Set<string>();
    selectedQuestions.forEach((q) => {
      if (q.dimension) set.add(q.dimension);
    });
    (existingFormDetail?.questions ?? []).forEach((q) => {
      if (q.dimension) set.add(q.dimension);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [selectedQuestions, existingFormDetail]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, loading]);

  useEffect(() => {
    if (!isOpen) return;

    setName("");
    setCategory("FREELANCER");
    setAllowedAnswererTypes(["ADMIN", "SUPERVISOR"]);
    setLoading(false);

    setUseExistingQuestions(false);
    setExistingForms([]);
    setExistingFormId("");
    setExistingFormDetail(null);
    setExistingChecked({});

    setNewType("SINGLE_CHOICE" as any);
    setNewPrompt("");
    setNewRequired(false);
    setNewDimension("");
    setNewMin("");
    setNewMax("");

    setSelectedQuestions([]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!useExistingQuestions) {
      setExistingForms([]);
      setExistingFormId("");
      setExistingFormDetail(null);
      setExistingChecked({});
      setSelectedQuestions((prev) => normalizeQuestionOrders(prev.filter((q) => q.source !== "existing")));
      return;
    }

    const loadForms = async () => {
      const reqId = ++reqRef.current.list;
      try {
        setLoading(true);
        const data = await listEvaluationFormsApi({
          includeArchived: false,
          includeQuestions: false,
        });
        if (reqId !== reqRef.current.list) return;

        setExistingForms(data);
        setExistingFormId((prev) => (prev && data.some((f) => f.id === prev) ? prev : data[0]?.id ?? ""));
      } catch {
        toast.error("Erro ao listar formulários para copiar perguntas.");
        setExistingForms([]);
        setExistingFormId("");
      } finally {
        if (reqId === reqRef.current.list) setLoading(false);
      }
    };

    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, useExistingQuestions]);

  // Carrega detalhe do formulário selecionado para copiar perguntas
  useEffect(() => {
    if (!isOpen) return;
    if (!useExistingQuestions) return;
    if (!existingFormId) {
      setExistingFormDetail(null);
      setExistingChecked({});
      return;
    }

    const loadDetail = async () => {
      const reqId = ++reqRef.current.detail;
      try {
        setLoading(true);
        const data = await getEvaluationFormByIdApi(existingFormId);
        if (reqId !== reqRef.current.detail) return;

        setExistingFormDetail(data);

        setExistingChecked({});
        setSelectedQuestions((prev) => normalizeQuestionOrders(prev.filter((q) => q.source !== "existing")));
      } catch {
        toast.error("Erro ao carregar formulário para copiar perguntas.");
        setExistingFormDetail(null);
        setExistingChecked({});
        setSelectedQuestions((prev) => normalizeQuestionOrders(prev.filter((q) => q.source !== "existing")));
      } finally {
        if (reqId === reqRef.current.detail) setLoading(false);
      }
    };

    loadDetail();
  }, [isOpen, useExistingQuestions, existingFormId]);

  const selectedSorted = selectedQuestions;

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const addExistingQuestion = (q: EvaluationQuestion) => {
    const draft: DraftQuestion = {
      key: `existing:${q.id}`,
      source: "existing",
      refId: q.id,
      type: q.type as any,
      prompt: q.prompt ?? "",
      required: !!q.required,
      order: 0,
      dimension: q.dimension ?? undefined,
      minValue: q.minValue == null ? undefined : Number(q.minValue),
      maxValue: q.maxValue == null ? undefined : Number(q.maxValue),
      options: (q.options ?? []).map((o: EvaluationQuestionOption) => ({
        label: o.label ?? "",
        value: o.value ?? undefined,
        order: Number(o.order ?? 0),
      })),
    };

    setSelectedQuestions((prev) => {
      if (prev.some((x) => x.key === draft.key)) return prev;
      return normalizeQuestionOrders([...prev, draft]);
    });
  };

  const removeQuestionByKey = (key: string) => {
    setSelectedQuestions((prev) => normalizeQuestionOrders(prev.filter((q) => q.key !== key)));
    if (key.startsWith("existing:")) {
      const id = key.replace("existing:", "");
      setExistingChecked((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
    }
  };

  const handleToggleExisting = (q: EvaluationQuestion, checked: boolean) => {
    setExistingChecked((s) => ({ ...s, [q.id]: checked }));
    if (checked) addExistingQuestion(q);
    else removeQuestionByKey(`existing:${q.id}`);
  };

  const handleAddNewOption = () => {
    if (!isChoiceType(newType)) return;
    if (loading) return;
    if (!optLabel.trim()) {
      toast.error("Informe o label da opção.");
      return;
    }
    const next: DraftOption = {
      key: uid(),
      label: optLabel.trim(),
      value: optValue.trim() ? optValue.trim() : undefined,
      order: 0,
    };
    setNewOptions((prev) => normalizeOptionOrders([...prev, next]));
    setOptLabel("");
    setOptValue("");
  };

  const handleAddNewQuestion = () => {
    if (loading) return;

    const prompt = newPrompt.trim();
    if (!prompt) {
      toast.error("Informe o prompt da pergunta.");
      return;
    }

    const minValue = newMin.trim() === "" ? undefined : Number(newMin);
    const maxValue = newMax.trim() === "" ? undefined : Number(newMax);

    if (minValue !== undefined && Number.isNaN(minValue)) {
      toast.error("minValue inválido.");
      return;
    }
    if (maxValue !== undefined && Number.isNaN(maxValue)) {
      toast.error("maxValue inválido.");
      return;
    }
    if (isScaleType(newType) && minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
      toast.error("minValue não pode ser maior que maxValue.");
      return;
    }
    if (isChoiceType(newType) && newOptions.length === 0) {
      toast.error("Pergunta de escolha deve ter pelo menos 1 opção.");
      return;
    }

    const q: DraftQuestion = {
      key: `new:${uid()}`,
      source: "new",
      type: newType,
      prompt,
      required: newRequired,
      order: 0,
      dimension: newDimension.trim() || undefined,
      minValue,
      maxValue,
      options: isChoiceType(newType)
        ? normalizeOptionOrders(newOptions).map((o) => ({ label: o.label, value: o.value, order: o.order }))
        : undefined,
    };

    setSelectedQuestions((prev) => normalizeQuestionOrders([...prev, q]));

    setNewPrompt("");
    setNewRequired(false);
    setNewDimension("");
    setNewMin("");
    setNewMax("");
    setNewOptions([]);
    setOptLabel("");
    setOptValue("");

    toast.success("Pergunta adicionada ao formulário.");
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const trimmedName = name.trim();
    if (!isAnonymousCategory && allowedAnswererTypes.length === 0) {
      toast.error("Selecione pelo menos um tipo de usuário que pode responder.");
      return;
    }

    const questions: CreateEvaluationFormRequest["questions"] =
      selectedQuestions.length > 0
        ? selectedQuestions.map((q) => ({
            type: q.type,
            prompt: q.prompt,
            required: q.required,
            order: q.order,
            dimension: q.dimension,
            minValue: q.minValue,
            maxValue: q.maxValue,
            options: q.options?.map((o) => ({
              label: o.label,
              value: o.value,
              order: o.order,
            })),
          }))
        : undefined;

    try {
      setLoading(true);
      const created = await createEvaluationFormApi({
        name: trimmedName,
        category,
        // NR1: resposta é anônima via link público, sem restrição de tipo de usuário
        allowedAnswererTypes: isAnonymousCategory ? [] : allowedAnswererTypes,
        questions,
      });
      toast.success("Formulário criado com sucesso!");
      onCreated?.(created);
      onClose();
    } catch {
      toast.error("Erro ao criar formulário.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="EditSupervisorOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="EditSupervisorShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        
        {/* Header */}
        <div className="EditSupervisorHeader">
          <div className="EditSupervisorHeaderLeft">
            <div>
              <h2 className="EditSupervisorHeaderTitle">Criar Formulário</h2>
              <p className="EditSupervisorHeaderSub">
                Cadastre um novo questionário de avaliação
              </p>
            </div>
          </div>
          <button
            type="button"
            className="EditSupervisorHeaderClose"
            onClick={onClose}
            title="Fechar"
            disabled={loading}
          >
            <XIcon />
          </button>
        </div>

        <form className="EditSupervisorBody" onSubmit={handleSubmit}>

          <div className="EditSupervisorFormBox">
            <p className="EditSupervisorFormBoxTitle">Dados do formulário</p>
            <div className="EditSupervisorFormGrid">
              <div>
                <label className="EditSupervisorFormLabel">Nome</label>
                <input
                  className="EditSupervisorFormInput"
                  placeholder="Ex.: Avaliação de Freelancer"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel">Tipo de formulário</label>
                <select
                  className="EditSupervisorFormInput"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as "LOJA" | "FREELANCER" | "NR1")}
                  disabled={loading}
                >
                  <option value="FREELANCER">Freelancer</option>
                  <option value="LOJA">Loja</option>
                  <option value="NR1">NR1 (pesquisa anônima)</option>
                </select>
              </div>
            </div>
          </div>

          {isAnonymousCategory ? (
            <div className="EditSupervisorFormBox" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
                🔒 Formulário NR1: respondido anonimamente por link público de uso único —
                não há restrição por tipo de usuário.
              </p>
            </div>
          ) : (
          <div className="EditSupervisorFormBox" style={{ marginTop: 16 }}>
            <p className="EditSupervisorFormBoxTitle">Quem pode responder (Tipos de Usuário) *</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              {(["ADMIN", "SUPERVISOR", "SUPERVISOR_DE_CAMPO", "FREELANCER"] as EvaluationAnswererType[]).map((type) => {
                const checked = allowedAnswererTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    className={`EditSupervisorStoreItem ${checked ? "checked" : ""}`}
                    onClick={() => {
                      if (checked) {
                        setAllowedAnswererTypes((prev) => prev.filter((t) => t !== type));
                      } else {
                        setAllowedAnswererTypes((prev) => [...prev, type]);
                      }
                    }}
                    disabled={loading}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      className="EditSupervisorStoreCheckbox"
                    />
                    <div className="EditSupervisorStoreInfo">
                      <p className="EditSupervisorStoreName">{type.replace(/_/g, " ")}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="EvaluationModalChecks" style={{ marginTop: 16 }}>
            <button
              type="button"
              className={`EditSupervisorStoreItem ${useExistingQuestions ? "checked" : ""}`}
              onClick={() => setUseExistingQuestions(!useExistingQuestions)}
              disabled={loading}
              style={{ width: "100%" }}
            >
              <input
                type="checkbox"
                checked={useExistingQuestions}
                readOnly
                className="EditSupervisorStoreCheckbox"
              />
              <div className="EditSupervisorStoreInfo">
                <p className="EditSupervisorStoreName">Selecionar perguntas existentes (copiar de outro formulário)</p>
              </div>
            </button>
          </div>

          {useExistingQuestions ? (
            <>
              <div className="EditSupervisorFormBox" style={{ marginTop: 16 }}>
                <p className="EditSupervisorFormBoxTitle">Copiar de Formulário</p>
                <div className="EditSupervisorFormGrid">
                  <div>
                    <label className="EditSupervisorFormLabel">Formulário de origem</label>
                    <select
                      className="EditSupervisorFormInput"
                      value={existingFormId}
                      onChange={(e) => setExistingFormId(e.target.value)}
                      disabled={loading || existingForms.length === 0}
                    >
                      {existingForms.length === 0 ? (
                        <option value="">Nenhum encontrado</option>
                      ) : (
                        existingForms.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.title}
                            {f.archivedAt ? " (ARQUIVADO)" : ""}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>

              <div className="EditSupervisorStoresBox" style={{ marginTop: 16 }}>
                <p className="EditSupervisorFormBoxTitle">Perguntas disponíveis</p>
                {existingFormDetail?.questions?.length ? (
                  <div className="EvaluationModalQuestions">
                    {existingFormDetail.questions
                      .slice()
                      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                      .map((q) => (
                        <label key={q.id} className="EvaluationModalCheck">
                          <input
                            type="checkbox"
                            checked={!!existingChecked[q.id]}
                            onChange={(e) => handleToggleExisting(q, e.target.checked)}
                            disabled={loading}
                          />
                          {q.order ?? 0}ª — {getEvaluationQuestionTypeLabel(q.type as any)} —{" "}
                          {q.prompt?.length > 80 ? `${q.prompt.slice(0, 80)}...` : q.prompt}
                        </label>
                      ))}
                  </div>
                ) : (
                  <div className="EvaluationModalHint">Nenhuma pergunta encontrada no formulário de origem.</div>
                )}
              </div>

              <div className="EvaluationModalDivider" />
            </>
          ) : null}

          <div className="EditSupervisorFormBox" style={{ marginTop: 16 }}>
            <p className="EditSupervisorFormBoxTitle">Criar nova pergunta</p>

          <div className="EditSupervisorFormGrid">
            <div>
              <label className="EditSupervisorFormLabel">Tipo</label>
              <select
                className="EditSupervisorFormInput"
                value={String(newType)}
                onChange={(e) => {
                  const t = e.target.value as any;
                  setNewType(t);
                  setNewMin("");
                  setNewMax("");
                  setNewOptions([]);
                }}
                disabled={loading}
              >
                <option value="TEXT">{getEvaluationQuestionTypeLabel("TEXT" as any)}</option>
                <option value="SINGLE_CHOICE">{getEvaluationQuestionTypeLabel("SINGLE_CHOICE" as any)}</option>
                <option value="MULTI_CHOICE">{getEvaluationQuestionTypeLabel("MULTI_CHOICE" as any)}</option>
                <option value="SCALE">{getEvaluationQuestionTypeLabel("SCALE" as any)}</option>
                <option value="BOOLEAN">{getEvaluationQuestionTypeLabel("BOOLEAN" as any)}</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="EditSupervisorFormLabel">Pergunta</label>
            <textarea
              className="EditSupervisorFormInput"
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              disabled={loading}
              style={{ height: "60px", padding: "8px 12px" }}
            />
          </div>

          <div className="EvaluationModalChecks">
            <label className="EvaluationModalCheck">
              <input
                type="checkbox"
                checked={newRequired}
                onChange={(e) => setNewRequired(e.target.checked)}
                disabled={loading}
              />
              Obrigatória
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="EditSupervisorFormLabel">Dimensão de risco (NR1, opcional)</label>
            <input
              className="EditSupervisorFormInput"
              placeholder="Ex.: Demandas, Controle, Apoio, Relacionamentos"
              value={newDimension}
              onChange={(e) => setNewDimension(e.target.value)}
              disabled={loading}
              list="nr1-dimension-suggestions-create"
            />
            <datalist id="nr1-dimension-suggestions-create">
              {dimensionSuggestions.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </div>

          {isScaleType(newType) ? (
            <div className="EditSupervisorFormGrid" style={{ marginTop: 12 }}>
              <div>
                <label className="EditSupervisorFormLabel">Valor Mínimo</label>
                <input
                  className="EditSupervisorFormInput"
                  type="number"
                  value={newMin}
                  onChange={(e) => setNewMin(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel">Valor Máximo</label>
                <input
                  className="EditSupervisorFormInput"
                  type="number"
                  value={newMax}
                  onChange={(e) => setNewMax(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          ) : null}

          {isChoiceType(newType) ? (
            <>
              <div className="EvaluationModalDivider" />
              <p className="EditSupervisorFormBoxTitle">Opções da pergunta</p>

              <div className="EditSupervisorFormGrid">
                <div>
                  <label className="EditSupervisorFormLabel">Opção</label>
                  <input
                    className="EditSupervisorFormInput"
                    value={optLabel}
                    onChange={(e) => setOptLabel(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    type="button"
                    className="EditSupervisorSearchButton"
                    onClick={handleAddNewOption}
                    disabled={loading || !optLabel.trim()}
                    style={{ height: 38, width: "100%" }}
                  >
                    Adicionar opção
                  </button>
                </div>
              </div>

              {newOptions.length ? (
                <div className="EvaluationModalOptionsList" style={{ marginTop: 12 }}>
                  {newOptions.map((o) => (
                    <div key={o.key} className="EvaluationModalOptionCard">
                      <div className="EditSupervisorFormGrid">
                        <div>
                          <label className="EditSupervisorFormLabel">Label</label>
                          <input
                            className="EditSupervisorFormInput"
                            value={o.label}
                            onChange={(e) =>
                              setNewOptions((prev) =>
                                normalizeOptionOrders(
                                  prev.map((x) => (x.key === o.key ? { ...x, label: e.target.value } : x))
                                )
                              )
                            }
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="EvaluationModalInlineActions" style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className="EditSupervisorModalButton Cancel"
                          onClick={() => setNewOptions((prev) => normalizeOptionOrders(prev.filter((x) => x.key !== o.key)))}
                          disabled={loading}
                          style={{ height: 32, padding: "0 16px" }}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="EvaluationModalHint">Adicione pelo menos 1 opção.</div>
              )}
            </>
          ) : null}

          <div className="EvaluationModalInlineActions" style={{ marginTop: 16 }}>
            <button type="button" className="EditSupervisorModalButton Confirm" onClick={handleAddNewQuestion} disabled={loading} style={{ width: "100%" }}>
              Adicionar pergunta ao formulário
            </button>
          </div>
          </div>

          <div className="EvaluationModalDivider" />

          <div className="EditSupervisorStoresBox" style={{ marginTop: 16 }}>
            <p className="EditSupervisorFormBoxTitle">Perguntas selecionadas</p>

          {selectedSorted.length === 0 ? (
            <div className="EvaluationModalHint">Nenhuma pergunta selecionada ainda.</div>
          ) : (
            <div className="EvaluationModalQuestions">
              {selectedSorted.map((q) => (
                <div key={q.key} className="EvaluationModalQuestionCard">
                  <div className="EvaluationModalQuestionHeader">
                    <div className="EvaluationModalQuestionTitle">
                      {q.order ?? 0}ª Pergunta - {q.prompt}
                    </div>
                    <div className="EvaluationModalQuestionMeta">
                      {getEvaluationQuestionTypeLabel(q.type as any)}
                      {q.required ? " • Obrigatória" : ""}
                      {q.source === "existing" ? " • Copiada" : " • Nova"}
                      {q.dimension ? ` • Dimensão: ${q.dimension}` : ""}
                      {isScaleType(q.type as any) && (q.minValue != null || q.maxValue != null)
                        ? ` • Valor mínimo = ${q.minValue ?? "-"} - Valor máximo = ${q.maxValue ?? "-"}`
                        : ""}
                    </div>
                  </div>

                  <div className="EvaluationModalInlineActions" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="EditSupervisorModalButton Cancel"
                      onClick={() => removeQuestionByKey(q.key)}
                      disabled={loading}
                      style={{ height: 32, padding: "0 16px" }}
                    >
                      Remover
                    </button>
                  </div>

                  {isChoiceType(q.type as any) ? (
                    (q.options?.length ?? 0) > 0 ? (
                      <ul className="EvaluationModalOptions">
                        {(q.options ?? [])
                          .slice()
                          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                          .map((o, idx) => (
                            <li key={`${q.key}:opt:${idx}`} className="EvaluationModalOption">
                              <span className="EvaluationModalOptionLabel">{o.label}</span>
                              {o.value ? <span className="EvaluationModalOptionValue"> — {o.value}</span> : null}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <div className="EvaluationModalHint">Sem opções.</div>
                    )
                  ) : null}
                </div>
              ))}
            </div>
          )}
          </div>

          <div className="EditSupervisorModalActions">
            <button type="button" className="EditSupervisorModalButton Cancel" onClick={onClose} disabled={loading}>
              Cancelar
            </button>

            <button type="submit" className="EditSupervisorModalButton Confirm" disabled={!canSubmit}>
              {loading ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvaluationFormModal;
